import { useSyncExternalStore } from 'react';

// FR-06: předvolba motivu, per prohlížeč/zařízení (žádný backend, neváže se na uživatele ani
// akademii, odhlášení ji nemaže - viz Bezpečnost a tenant isolation FR01 PART1). Stejný vzor jako
// `features/changelog/whats-new-storage.ts`.
const STORAGE_KEY = 'trenigo:theme-preference';

export type ThemePreference = 'light' | 'dark' | 'system';

const VALID_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function isThemePreference(value: string | null): value is ThemePreference {
  return value !== null && (VALID_PREFERENCES as readonly string[]).includes(value);
}

// Odznak/stav se čte ve více instancích UserMenu zároveň (mobilní lišta + postranní menu) a musí
// se sjednotit i mezi záložkami - sdílený odběr přes useSyncExternalStore, stejně jako
// `useHasUnseenChangelog`.
const listeners = new Set<() => void>();

// Záloha pro selhávající localStorage (private mode) - volba pak platí do reloadu (FR-06).
// Výchozí `system` odpovídá "chybějící nebo neplatná hodnota" před prvním zápisem.
let inMemoryPreference: ThemePreference = 'system';

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

/** Čtení může selhat (private mode) - stejná try/catch konvence jako `whats-new-storage.ts`. */
export function getThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemePreference(stored) ? stored : inMemoryPreference;
  } catch {
    return inMemoryPreference;
  }
}

export function setThemePreference(preference: ThemePreference): void {
  inMemoryPreference = preference;
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // FR-06 - selhání zápisu (private mode/zakázané úložiště) nesmí appku shodit, volba zůstává
    // platná jen v paměti do reloadu.
  }
  notifyListeners();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // FR-07 AC-09: změna předvolby v jedné záložce se má projevit i v ostatních otevřených záložkách
  // téhož prohlížeče.
  function handleStorage(event: StorageEvent) {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  }
  window.addEventListener('storage', handleStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', handleStorage);
  };
}

// Předrenderování a hydratace (apps/landing, FR01 20261001_LandingPage/002_Marketing FR-03): HTML
// ze serveru má neutrální předvolbu `system` a React ji použije i při hydrataci. Uloženou předvolbu
// vykreslí hned po hydrataci, bez hydration mismatch. Aplikace s createRoot server snapshot nečtou.
function getServerThemePreference(): ThemePreference {
  return 'system';
}

/** Reaktivní předvolba sdílená všemi instancemi UserMenu i mezi záložkami (FR-07 AC-09). */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getThemePreference, getServerThemePreference);
}
